import type { i18n as I18n } from 'i18next';
import { DEFAULT_LANGUAGE } from './i18n.constants';

/**
 * Canonical regional sibling for each non-English base language.
 *
 * Per PRNT-4468 (Slack design, 2026-06-29): each non-English language has
 * exactly one supported region variant. English is handled separately via
 * `fallbackLng: 'en-US'` and is not present in this map.
 */
export const CANONICAL_REGION_BY_BASE: ReadonlyRecord<string, string> = {
  de: 'de-DE',
  es: 'es-ES',
  fr: 'fr-FR',
  it: 'it-IT',
  ja: 'ja-JP',
};

/**
 * Look up the canonical regional sibling for a language code.
 *
 * Returns the canonical sibling for the input's base language (e.g. `de-AT`
 * → `de-DE`, bare `de` → `de-DE`, `De-At` → `de-DE`). Returns the input
 * unchanged when:
 *   - the input is not a string (defensive)
 *   - the input's base has no entry in `CANONICAL_REGION_BY_BASE` (e.g. `en`,
 *     `en-AU` — English is handled by `fallbackLng`, not this map)
 *
 * Used by:
 *   - `injectClosestSibling` (chain injection in the resolver wrapper)
 *   - `i18next-http-middleware`'s `detection.convertDetectedLanguage` hook,
 *     which canonicalizes Accept-Language detections before they hit the
 *     `supportedLngs` filter
 *
 * Both consumers read from the same `CANONICAL_REGION_BY_BASE` map — single
 * source of truth.
 */
export const canonicalizeLanguage = (lng: string): string => {
  if (typeof lng !== 'string') return lng;
  const base = lng.split('-')[0].toLowerCase();
  return CANONICAL_REGION_BY_BASE[base] ?? lng;
};

/**
 * Pure transform that injects the canonical regional sibling into a chain
 * returned by i18next's `toResolveHierarchy`.
 *
 * Handles both regional inputs (`de-AT`) and bare base inputs (`de`). The base
 * is lowercased before lookup, so `De-At` and `de-AT` resolve identically.
 *
 * No-op when:
 *   - `code` has no entry in `CANONICAL_REGION_BY_BASE` (e.g. `en`, `en-AU`)
 *   - the canonical sibling is already in `chain` (e.g. `de-DE` input)
 *
 * Inserts the canonical immediately before `defaultLanguage` (the fallback
 * terminus) so the resolution order is: exact → canonical sibling → en-US.
 * Appends to the end if `defaultLanguage` is absent from the chain.
 */
export const injectClosestSibling = (chain: ReadonlyArray<string>, code: string, defaultLanguage: string): ReadonlyArray<string> => {
  const canonical = canonicalizeLanguage(code);
  if (canonical === code || chain.includes(canonical)) return chain;
  const fallbackIdx = chain.findIndex(c => c === defaultLanguage);
  const insertAt = fallbackIdx >= 0 ? fallbackIdx : chain.length;
  return [...chain.slice(0, insertAt), canonical, ...chain.slice(insertAt)];
};

/**
 * Wraps `i18n.services.languageUtils.toResolveHierarchy` to inject canonical
 * regional siblings into every resolved chain. Must be called once after
 * `i18n.init()` has set up `services.languageUtils`.
 *
 * Idempotent across hot module reloads is not guaranteed — the function rewraps
 * each call. In production each process calls it once at boot, so this is fine.
 * In dev, HMR may stack wrappers; restart the dev server if behavior looks off.
 */
export const applyClosestSiblingResolver = (i18n: I18n): void => {
  const languageUtils = i18n.services.languageUtils;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const original = (languageUtils as any).toResolveHierarchy.bind(languageUtils);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (languageUtils as any).toResolveHierarchy = (code: string, fallbackCode?: string | string[]) => {
    const chain = original(code, fallbackCode) as ReadonlyArray<string>;
    return injectClosestSibling(chain, code, DEFAULT_LANGUAGE) as string[];
  };
};
